import DeleteIcon from '@mui/icons-material/Delete';
import { Box, InputAdornment, Snackbar, Stack, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useContext, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { GroupSettingsContext } from '../../contexts/GroupSettingsContext';
import { scrollbarStyle } from '../../shared/dashboard';
import theme from "../../theme";
import ConfirmDialog from '../modals/ConfirmDialog';
import Tag from "../molecules/dashboard-tags/Tag";
import { FallbackLoading } from '../templates/loader';

const useStyles = makeStyles(() => ({
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    group: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: 'capitalize',
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	border: `2px solid ${theme.colors.neutral['400']} !important`,
            },
            "&:hover fieldset": {
                border: `1px solid ${theme.colors.neutral['600']} !important`,
            },
            "&.Mui-focused fieldset": {
              	border: `2px solid ${theme.palette.primary.main} !important`,
            },
        },
    },
    formContainer: {
        minWidth: 60,
        padding: 0,
        border: 0,
        "& > .MuiInputBase-root": {
            "& > .MuiSelect-select": {
                fontSize: '1rem',
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                textTransform: 'capitalize',
                paddingTop: '0 !important',
                paddingBottom: '0 !important',
            },
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                fill: theme.colors.neutral['600'],
                "&.Mui-disabled": {
                    display: 'none',
                },
            },
            "& fieldset": {
                border: 0,
            },
        },
    },
    menu: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 400,
        color: 'gray',
    },
    closeIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.neutral['500'],
        cursor: 'pointer',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    deleteIcon: {
        cursor: 'pointer',
        color: theme.colors.neutral['500'],
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    addTag: {
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
        cursor: 'pointer',
        userSelect: 'none',
        paddingRight: 8,
    },
}))

const KeyPeopleTags: React.FC<{}> = () => {
    const classes = useStyles();
    const { keyPeopleTags, setKeyPeopleTags } = useContext(GroupSettingsContext);
    const [loading] = useState<boolean>(false);
    const [newTag, setNewTag] = useState<string>('')
    const [snackIsOpen, setSnackIsOpen] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [deletableTag, setDeletableTag] = useState<string>('');

    const debounced = useDebouncedCallback((value) => {
        setNewTag(value);
    }, 30, { maxWait: 100 });

    const handleCreateTag = useCallback(() => {
        if (!newTag) {
            setErrorMessage('Please enter a tag')
            setSnackIsOpen(true)
            return;
        }

        setKeyPeopleTags(prev => [newTag, ...prev]);
        setNewTag('');
        setErrorMessage('Successfully created new tag');
        setSnackIsOpen(true);
    }, [newTag, setKeyPeopleTags]);

    const handleDeleteTag = useCallback((tag: string) =>
        setKeyPeopleTags(prev => prev.filter(item => item !== tag))
    , [setKeyPeopleTags]);

    return (<>
        <Stack spacing={1} minWidth={400} maxWidth={400} width="100%" height="100%" p={1}>
            <Stack spacing={2} width="100%" height="100%" p={1}>
                <TextField variant="outlined"
                    className={classes.inputField}
                    placeholder={`Add team tags...`}
                    value={newTag}
                    disabled={loading}
                    onChange={(e) => debounced(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter')
                            handleCreateTag();
                    }}
                    sx={{ width: '100%' }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" onClick={handleCreateTag}>
                                <Typography className={classes.addTag}>Add</Typography>
                            </InputAdornment>
                        )}}
                />
                {loading ? (<FallbackLoading />) : !!keyPeopleTags?.length && (
                    <Stack width="100%" height="calc(67vh - 320px)">
                        <Stack spacing={2} width="100%" height="100%" pt={1} sx={{ overflowY: 'auto', ...scrollbarStyle }}>
                            {keyPeopleTags.map((tag, i) => (
                                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                                    key={'key-people-tags-150-' + i}>
                                    <Tag title={tag} color={theme.colors.neutral['100']} />
                                    <DeleteIcon className={classes.deleteIcon} onClick={() => {
                                        setDeletableTag(tag);
                                        setDialogOpen(true)
                                    }} />
                                </Stack>
                            ))}
                            <Box height={32}>&nbsp;</Box>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Stack>
        {snackIsOpen && (
            <Snackbar
                open={snackIsOpen}
                autoHideDuration={2000}
                onClose={() => setSnackIsOpen(false)}
                message={errorMessage}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            />
        )}
        {dialogOpen && (
            <ConfirmDialog
                title="Delete confirmation"
                content={`Are you sure you want to delete this ${deletableTag} tag?`}
                open={dialogOpen}
                confirmCallback={() => {
                    handleDeleteTag(deletableTag);
                    setDeletableTag('');
                    setDialogOpen(false)
                    setErrorMessage('Tag successfully deleted');
                    setSnackIsOpen(true)
                }}
                cancelCallback={() => setDialogOpen(false)}
            />
        )}
    </>);
}

export default KeyPeopleTags;